/**
 * Dashboard — the post-login landing page.
 *
 * Every figure on this page is computed from records that are really loaded;
 * `/api/dashboard` is a single round trip over the migrated Salesforce org.
 *
 * The page is organised around the Supplier Journey rather than around the
 * tables, because that is the question the business asks: how ready is the
 * portfolio, what does it need, who can supply it, and is delivery happening.
 *
 * Two editorial decisions worth keeping:
 *
 *   1. The readiness distribution leads. It is the least comfortable figure in
 *      the system — a median of a few percent against the instrument — and it is
 *      also the entire commercial argument for the programme. Burying it under
 *      vanity counts would make the dashboard decorative.
 *   2. Data-integrity findings are rendered as first-class cards, not hidden.
 *      Three dimensions that a normal CRM dashboard would chart are constant in
 *      the migrated data (referral outcome, enrollment stage, business status),
 *      and saying so plainly is more useful than a donut with one segment.
 */

import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import { StatCard } from '../organisms/StatCard';
import { DonutChart } from '../organisms/DonutChart';
import { ChartCard, Histogram, HBars, DualArea } from '../organisms/Charts';

interface DashboardResponse {
  totals: {
    businesses: number; people: number; assessments: number; referrals: number;
    enrollments: number; partners: number; programs: number; sessions: number;
    businessesAssessed: number;
  };
  readiness: {
    scored: number; mean: number | null; median: number | null; max: number | null;
    distribution: { lo: number; hi: number; count: number }[];
    byDomain: { domain: string; gates: number; met: number; answers: number; pctMet: number }[];
    criticalGates: { criterion: string; domain: string; met: number; answered: number }[];
  };
  demand: { capability: string; capabilityId: string | null; referrals: number; partnersOffering: number }[];
  timeline: { label: string; assessments: number; referrals: number }[];
  categories: { label: string; count: number }[];
  delivery: { records: number; attended: number; registered: number; unrecorded: number };
  integrity: { key: string; severity: string; title: string; detail: string }[];
}

const CATEGORY_COLORS = [
  'var(--brand-primary)', 'var(--brand-secondary)', 'var(--brand-accent)',
  '#3D6B4F', '#A8D5A2', '#D97706', '#64748B', '#8C6D3F', '#CFD5D1',
];

const SEVERITY_STYLE: Record<string, { bg: string; fg: string; label: string }> = {
  high: { bg: 'var(--color-status-amber-bg)', fg: 'var(--color-status-amber)', label: 'Needs attention' },
  info: { bg: 'var(--color-status-gray-bg)', fg: 'var(--color-ink-3)', label: 'For information' },
};

/**
 * The procurement-critical gates, as a grid rather than a bar chart.
 *
 * Every one of these is currently met by zero businesses, so plotting them
 * produces identical full-width bars — geometry carrying no information. A grid
 * of "0 / 165" cells says the same thing faster, and it stays readable on the
 * day some of them start moving, which is the whole point of the programme.
 */
function CriticalGates({ gates }: { gates: { criterion: string; domain: string; met: number; answered: number }[] }) {
  const answered = gates[0]?.answered ?? 0;
  const unmet = gates.filter((g) => g.met === 0).length;
  const allUnmet = unmet === gates.length;

  return (
    <section
      aria-labelledby="gates-heading"
      style={{
        background: allUnmet ? 'var(--color-status-red-bg)' : 'var(--color-card)',
        border: `1px solid ${allUnmet ? 'var(--color-status-red)' : 'var(--color-border)'}`,
        borderRadius: 'var(--r-2xl)',
        padding: 'var(--sp-5)',
      }}
    >
      <header style={{ marginBottom: 'var(--sp-4)' }}>
        <h2 id="gates-heading" style={{ margin: 0, fontSize: 'var(--fs-lg)', fontWeight: 'var(--fw-semibold)', color: 'var(--color-ink)' }}>
          Procurement gates
        </h2>
        <p style={{ margin: '4px 0 0', fontSize: 'var(--fs-sm)', color: 'var(--color-ink-2)', lineHeight: 'var(--lh-normal)', maxWidth: 760 }}>
          The credentials a corporate buyer checks before a supplier can be onboarded at all.{' '}
          {allUnmet ? (
            <strong style={{ color: 'var(--color-status-red)' }}>
              All {gates.length} are met by none of the {answered} assessed businesses.
            </strong>
          ) : (
            <strong style={{ color: 'var(--color-ink)' }}>
              {gates.length - unmet} of {gates.length} are met by at least one business.
            </strong>
          )}{' '}
          This is the shortest, most actionable list in the system.{' '}
          <Link to="/reports/procurement-gates" style={{ color: 'var(--brand-primary)' }}>
            Open the full report →
          </Link>
        </p>
      </header>

      <ul style={{
        display: 'grid', gap: 'var(--sp-2)', margin: 0, padding: 0, listStyle: 'none',
        gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 236px), 1fr))',
      }}>
        {gates.map((g) => (
          <li key={g.criterion}>
          <Link
            to={`/assessment-criteria?domain=${encodeURIComponent(g.domain)}`}
            className="ig-interactive"
            style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 'var(--sp-2)',
              background: 'var(--color-card)', border: '1px solid var(--color-border)',
              borderRadius: 'var(--r-md)', padding: '9px 12px',
              textDecoration: 'none', color: 'inherit',
            }}
          >
            <span style={{ fontSize: 'var(--fs-sm)', color: 'var(--color-ink)', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={`${g.criterion} — ${g.domain}`}>
              {g.criterion}
            </span>
            <span style={{
              fontSize: 'var(--fs-sm)', fontWeight: 'var(--fw-bold)', fontVariantNumeric: 'tabular-nums',
              color: g.met === 0 ? 'var(--color-status-red)' : 'var(--color-status-green)', whiteSpace: 'nowrap',
            }}>
              {g.met}<span style={{ color: 'var(--color-muted)', fontWeight: 'var(--fw-normal)' }}> / {g.answered}</span>
            </span>
          </Link>
          </li>
        ))}
      </ul>
    </section>
  );
}

/**
 * A KPI tile that leads somewhere. The card keeps its own styling; this only
 * supplies the anchor, so the whole tile is one focusable, middle-clickable
 * target rather than a div listening for clicks.
 */
function KpiLink({ to, children }: { to: string; children: React.ReactNode }) {
  // The class goes on the ANCHOR, not the card: the anchor is what receives
  // hover and keyboard focus, so styling the card would light up on hover and
  // stay dark on Tab.
  return (
    <Link to={to} className="ig-interactive" style={{ textDecoration: 'none', color: 'inherit', display: 'block', borderRadius: 'var(--r-lg)' }}>
      {children}
    </Link>
  );
}

export default function Dashboard() {
  const navigate = useNavigate();
  const query = useQuery<DashboardResponse>({
    queryKey: ['dashboard'],
    queryFn: ({ signal }) => api.get<DashboardResponse>('/api/dashboard', signal),
  });

  if (query.isLoading) {
    return <p style={{ color: 'var(--color-ink-3)' }}>Loading…</p>;
  }
  if (query.isError || !query.data) {
    return (
      <div style={{
        padding: 'var(--sp-4)', borderRadius: 'var(--r-lg)',
        background: 'var(--color-status-red-bg)', color: 'var(--color-status-red)', fontSize: 'var(--fs-md)',
      }}>
        Could not load the dashboard. {(query.error as Error)?.message ?? ''}
      </div>
    );
  }

  const d = query.data;
  const t = d.totals;
  const pct = (n: number | null) => (n === null ? '—' : `${n}%`);

  const attendanceRate = d.delivery.records
    ? Math.round((d.delivery.attended / d.delivery.records) * 100)
    : null;

  const unmetCapabilities = d.demand.filter((x) => x.partnersOffering === 0).length;

  return (
    <div style={{ display: 'grid', gap: 'var(--sp-6)' }}>
      {/* ── header ───────────────────────────────────────────────────────── */}
      <header>
        <h1 style={{
          margin: 0, fontSize: 'var(--fs-3xl)', fontWeight: 'var(--fw-semibold)',
          letterSpacing: '-0.02em', color: 'var(--color-ink)',
        }}>
          The Supplier Journey
        </h1>
        <p style={{ margin: '6px 0 0', color: 'var(--color-ink-3)', fontSize: 'var(--fs-md)', maxWidth: 760, lineHeight: 'var(--lh-normal)' }}>
          Every figure below is computed from the {t.businesses.toLocaleString()} businesses migrated
          from Salesforce — nothing here is illustrative.
        </p>
        {/* Stage status, stated rather than implied. Listing all five as a live
            pipeline reads as "all five work"; two do not yet, and a reader who
            discovers that themselves discounts everything else on the page. */}
        <ol style={{
          display: 'flex', flexWrap: 'wrap', gap: 'var(--sp-2)', listStyle: 'none',
          margin: 'var(--sp-3) 0 0', padding: 0,
        }}>
          {[
            { name: 'Assess', state: 'live' },
            { name: 'Connect', state: 'live' },
            { name: 'Develop', state: 'live' },
            { name: 'Validate', state: 'awaiting first reassessment' },
            { name: 'Procure', state: 'not built' },
          ].map((st) => {
            const live = st.state === 'live';
            return (
              <li key={st.name} style={{
                display: 'inline-flex', alignItems: 'baseline', gap: 6,
                padding: '4px 10px', borderRadius: 999,
                fontSize: 'var(--fs-xs)', fontWeight: 'var(--fw-medium)',
                background: live ? 'var(--brand-primary-bg)' : 'var(--color-status-gray-bg)',
                color: live ? 'var(--brand-primary)' : 'var(--color-ink-3)',
                border: `1px solid ${live ? 'var(--brand-primary)' : 'var(--color-border)'}`,
              }}>
                <strong>{st.name}</strong>
                <span style={{ fontWeight: 'var(--fw-normal)', opacity: 0.85 }}>
                  {live ? 'live' : st.state}
                </span>
              </li>
            );
          })}
        </ol>
      </header>

      {/* ── KPI row ──────────────────────────────────────────────────────── */}
      <section
        aria-label="Portfolio totals"
        style={{ display: 'grid', gap: 'var(--sp-3)', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 148px), 1fr))' }}
      >
        <KpiLink to="/clients"><StatCard label="Businesses" value={t.businesses.toLocaleString()} caption="in the register" /></KpiLink>
        <KpiLink to="/contacts"><StatCard label="People" value={t.people.toLocaleString()} caption="across those businesses" /></KpiLink>
        {/* BUSINESSES assessed, not assessment records. Dividing the record count
            by the register reported 5% coverage when the true figure is ~2%. */}
        <KpiLink to="/assessments"><StatCard
          label="Businesses assessed"
          value={t.businessesAssessed.toLocaleString()}
          caption={`${((t.businessesAssessed / Math.max(1, t.businesses)) * 100).toFixed(1)}% of the register · ${t.assessments.toLocaleString()} assessment records`}
          accent="amber"
        /></KpiLink>
        <KpiLink to="/referrals"><StatCard label="Referrals" value={t.referrals.toLocaleString()} caption="gap → partner" /></KpiLink>
        <KpiLink to="/enrollments"><StatCard label="Enrollments" value={t.enrollments.toLocaleString()} caption={`across ${t.programs} programmes`} /></KpiLink>
        <KpiLink to="/partners"><StatCard label="Partners" value={t.partners.toLocaleString()} caption="ecosystem directory" accent={unmetCapabilities > 0 ? 'red' : 'hunter'} /></KpiLink>
      </section>

      {/* ── the headline: readiness ──────────────────────────────────────── */}
      <section style={{ display: 'grid', gap: 'var(--sp-4)', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 420px), 1fr))' }}>
        <ChartCard
          title="Readiness across the assessed portfolio"
          caption={
            <>
              Share of the instrument each business affirmatively meets.
              Median <strong style={{ color: 'var(--color-ink)' }}>{pct(d.readiness.median)}</strong>,
              best <strong style={{ color: 'var(--color-ink)' }}>{pct(d.readiness.max)}</strong> of {d.readiness.scored} scored.
            </>
          }
        >
          <Histogram
            bins={d.readiness.distribution}
            markerAt={d.readiness.median}
            markerLabel={`median ${pct(d.readiness.median)}`}
            ariaLabel={`Readiness distribution: ${d.readiness.distribution.filter((b) => b.count).map((b) => `${b.lo} to ${b.hi} percent, ${b.count} businesses`).join('; ')}`}
          />
          <p style={{ margin: '10px 0 0', fontSize: 'var(--fs-sm)', color: 'var(--color-ink-3)', lineHeight: 'var(--lh-normal)' }}>
            Not one assessed business clears half the instrument. This is the gap the programme exists to close,
            and the baseline any pilot will be measured against.
          </p>
        </ChartCard>

        <ChartCard
          title="Readiness by domain"
          caption={
            <>
              Share of each domain's gates that businesses meet. This is where curriculum goes.{' '}
              <Link to="/reports/readiness-gaps" style={{ color: 'var(--brand-primary)' }}>
                See all {d.readiness.byDomain.reduce((s, x) => s + x.gates, 0)} criteria →
              </Link>
            </>
          }
        >
          <HBars
            data={d.readiness.byDomain.map((g) => ({
              label: `${g.domain} · ${g.gates} gates`,
              value: g.pctMet,
              href: `/assessment-criteria?domain=${encodeURIComponent(g.domain)}`,
            }))}
            valueLabel="% of gates met across the assessed portfolio"
            ariaLabel={`Readiness by domain: ${d.readiness.byDomain.map((g) => `${g.domain}, ${g.pctMet} percent of ${g.gates} gates met`).join('; ')}`}
          />
        </ChartCard>
      </section>

      {/* ── the procurement gates ────────────────────────────────────────── */}
      {d.readiness.criticalGates.length > 0 && <CriticalGates gates={d.readiness.criticalGates} />}

      {/* ── demand vs supply, and volume over time ───────────────────────── */}
      <section style={{ display: 'grid', gap: 'var(--sp-4)', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 420px), 1fr))' }}>
        <ChartCard
          title="What businesses ask for — and who can supply it"
          caption={
            unmetCapabilities > 0
              ? <>Referrals raised per capability, against partners who have declared it. <strong style={{ color: 'var(--color-status-red)' }}>{unmetCapabilities} of {d.demand.length}</strong> have no declared supplier.</>
              : <>Referrals raised per capability, against partners who have declared it.</>
          }
        >
          <HBars
            data={d.demand.map((x) => ({
              label: x.capability,
              value: x.referrals,
              secondary: x.partnersOffering,
              href: x.capabilityId ? `/referrals?capability_id=${encodeURIComponent(x.capabilityId)}` : undefined,
            }))}
            valueLabel="referrals raised"
            secondaryLabel="partners offering"
            ariaLabel={`Capability demand versus supply: ${d.demand.map((x) => `${x.capability}, ${x.referrals} referrals, ${x.partnersOffering} partners`).join('; ')}`}
          />
        </ChartCard>

        <ChartCard
          title="Volume by quarter"
          caption="Assessments and referrals on their source dates, not migration timestamps."
        >
          <DualArea
            points={d.timeline.map((p) => ({ label: p.label, a: p.referrals, b: p.assessments }))}
            aLabel="Referrals"
            bLabel="Assessments"
            ariaLabel={`Quarterly volume: ${d.timeline.map((p) => `${p.label}, ${p.referrals} referrals and ${p.assessments} assessments`).join('; ')}`}
          />
        </ChartCard>
      </section>

      {/* ── portfolio mix + delivery ─────────────────────────────────────── */}
      <section style={{ display: 'grid', gap: 'var(--sp-4)', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 320px), 1fr))' }}>
        <DonutChart
          title="Sector mix"
          caption="Self-reported sector across the register — pick one to see those businesses"
          segments={d.categories.map((c, i) => ({
            label: c.label,
            value: c.count,
            color: CATEGORY_COLORS[i % CATEGORY_COLORS.length],
          }))}
          centerLabel="Businesses"
          // DonutChart already supports an activatable segment; 'Not recorded' is a
          // synthesised bucket with no filterable value, so it stays inert rather
          // than linking to a query that would return the whole register.
          onSegmentClick={(seg) => {
            if (seg.label === 'Not recorded') return;
            navigate(`/clients?category=${encodeURIComponent(seg.label)}`);
          }}
        />

        <ChartCard title="Delivery" caption="Coaching and workshop attendance recorded against sessions.">
          <div style={{ display: 'grid', gap: 'var(--sp-3)' }}>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 'var(--sp-2)' }}>
              <span style={{ fontSize: 40, fontWeight: 'var(--fw-bold)', color: 'var(--color-ink)', lineHeight: 1 }}>
                {attendanceRate === null ? '—' : `${attendanceRate}%`}
              </span>
              <span style={{ fontSize: 'var(--fs-sm)', color: 'var(--color-ink-3)' }}>attended</span>
            </div>
            <dl style={{ display: 'grid', gap: 6, margin: 0, fontSize: 'var(--fs-sm)' }}>
              {([
                ['Sessions delivered', t.sessions, '/program-sessions'],
                ['Attendance records', d.delivery.records, '/attendance'],
                ['Attended', d.delivery.attended, '/attendance?status=Attended'],
                ['Registered, not marked', d.delivery.registered, '/attendance?status=Registered'],
              ] as [string, number, string][]).map(([k, v, to]) => (
                <Link key={k} to={to} className="ig-bar-row" style={{ display: 'flex', justifyContent: 'space-between', gap: 'var(--sp-3)', textDecoration: 'none', color: 'inherit' }}>
                  <dt style={{ color: 'var(--color-ink-3)' }}>{k}</dt>
                  <dd style={{ margin: 0, fontWeight: 'var(--fw-semibold)', fontVariantNumeric: 'tabular-nums' }}>
                    {Number(v).toLocaleString()}
                  </dd>
                </Link>
              ))}
            </dl>
          </div>
        </ChartCard>
      </section>

      {/* ── integrity signals ────────────────────────────────────────────── */}
      {d.integrity.length > 0 && (
        <section aria-labelledby="integrity-heading">
          <h2 id="integrity-heading" style={{ margin: '0 0 var(--sp-3)', fontSize: 'var(--fs-lg)', fontWeight: 'var(--fw-semibold)' }}>
            What the migration could not measure
          </h2>
          <div style={{ display: 'grid', gap: 'var(--sp-3)', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 300px), 1fr))' }}>
            {d.integrity.map((s) => {
              const style = SEVERITY_STYLE[s.severity] ?? SEVERITY_STYLE.info;
              return (
                <article
                  key={s.key}
                  style={{
                    background: 'var(--color-card)', border: '1px solid var(--color-border)',
                    borderLeft: `3px solid ${style.fg}`, borderRadius: 'var(--r-lg)', padding: 'var(--sp-4)',
                  }}
                >
                  <span style={{
                    display: 'inline-block', fontSize: 'var(--fs-xs)', fontWeight: 'var(--fw-semibold)',
                    textTransform: 'uppercase', letterSpacing: '0.04em',
                    background: style.bg, color: style.fg, padding: '2px 8px', borderRadius: 999, marginBottom: 8,
                  }}>
                    {style.label}
                  </span>
                  <h3 style={{ margin: '0 0 4px', fontSize: 'var(--fs-md)', fontWeight: 'var(--fw-semibold)', color: 'var(--color-ink)' }}>
                    {s.title}
                  </h3>
                  <p style={{ margin: 0, fontSize: 'var(--fs-sm)', color: 'var(--color-ink-3)', lineHeight: 'var(--lh-normal)' }}>
                    {s.detail}
                  </p>
                </article>
              );
            })}
          </div>
        </section>
      )}

      <nav aria-label="Jump to" style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--sp-2)' }}>
        {[
          ['Businesses', '/clients'],
          ['Assessments', '/assessments'],
          ['Referrals', '/referrals'],
          ['Partners', '/partners'],
          ['Programmes', '/programs'],
          ['Reports', '/reports'],
        ].map(([label, to]) => (
          <Link
            key={to}
            to={to}
            style={{
              padding: '7px 14px', fontSize: 'var(--fs-sm)', fontWeight: 'var(--fw-medium)',
              textDecoration: 'none', color: 'var(--color-ink-2)',
              background: 'var(--color-card)', border: '1px solid var(--color-border)', borderRadius: 999,
            }}
          >
            {label}
          </Link>
        ))}
      </nav>
    </div>
  );
}
